// src/app/accounting/sales-receipt-print/create-sales-receipt-pdf.ts
import jsPDF from 'jspdf';
import { SalesReceipt } from 'src/app/shared/models/accounting';
import { CompanyInfo } from 'src/app/shared/models/company';

export class SalesReceiptPdfGenerator {
  /**
   * @param logoDataUri Pre-resolved logo bytes. Blob-backed logos live behind a
   * SAS URL, which jsPDF cannot embed — callers resolve it via
   * SharedService.getCompanyLogoDataUri() and pass the result here.
   */
  generateReceiptPdf(receiptData: any, company_info: CompanyInfo, logoDataUri?: string): jsPDF {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'pt',
      format: 'a5', // Smaller page size
      compress: true, // Enable compression
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20; // Decreased from 40 to 20 for smaller side margins
    let yPosition = 40;
    const mainFont = 'helvetica'; // Lighter font
    const mainFontSize = 8

    const titlePrefix =
      company_info?.company_name?.trim() || company_info?.legal_name?.trim() || '';
    const receiptTitle = titlePrefix ? `${titlePrefix} Sales Receipt` : 'Sales Receipt';

    // Header
    doc.setFontSize(14); // Reduced font size
    doc.setFont(mainFont, 'bold');
    doc.text(receiptTitle, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 30;

    // Set a smaller font size for all other text
    doc.setFontSize(mainFontSize);

    // Company Info
    const companyInfo = {
      name: company_info?.company_name || '',
      street: company_info?.address2
        ? `${company_info.address || ''}, ${company_info.address2 || ''}`
        : company_info?.address || '',
      cityStateZip: `${company_info?.city || ''},${
        company_info?.state || ''
      }, ${company_info?.zip_code || ''}`,
      phone: company_info?.phone_number || '',
      email: company_info?.customer_facing_email || '',
      website: company_info?.website || '',
      logo: logoDataUri || company_info?.company_logo || '',
    };

    doc.setFontSize(mainFontSize); // Smaller font
    doc.setFont(mainFont, 'bold');
    if (companyInfo.name) doc.text(companyInfo.name, margin, yPosition);
    yPosition += 12;
    doc.setFont(mainFont, 'normal');

    const companyDetails = [
      companyInfo.street,
      companyInfo.cityStateZip,
      companyInfo.phone,
      companyInfo.email,
    ];
    companyDetails.forEach((line) => {
      if (line) {
        doc.text(line, margin, yPosition);
        yPosition += 12;
      }
    });

    // Bill To
    yPosition += 8;
    doc.setFont(mainFont, 'bold');
    doc.text('Bill To:', margin, yPosition);
    yPosition += 12;
    doc.setFont(mainFont, 'normal');
    const billing = receiptData?.billing_address || {};
    const cityStateZip = [billing.city, billing.state, billing.zip_code]
      .filter(Boolean)
      .join(', ')
      .replace(/, $/, '');

    const billTo = [
      receiptData?.customer_name || '',
      `${billing.street || ''}`.trim(),
      cityStateZip,
    ];
    billTo.forEach((line) => {
      if (line) {
        doc.text(line, margin, yPosition);
        yPosition += 12;
      }
    });

    // Right Column (Logo and Sales Info)
    const logoWidth = 60;
    const logoHeight = 60;
    const rightColumnX = pageWidth - margin - logoWidth;
    if (companyInfo.logo && companyInfo.logo.startsWith('data:image')) {
      doc.addImage(
        companyInfo.logo,
        companyInfo.logo.startsWith('data:image/jpeg') ? 'JPEG' : 'PNG',
        rightColumnX,
        50,
        logoWidth,
        logoHeight
      ); // Logo aligned right
    }
    doc.setFont(mainFont, 'normal');
    const salesInfoY = 120;
    if (receiptData?.sales_receipt_number) {
      doc.text(
        `Sales #${receiptData.sales_receipt_number}`,
        pageWidth - margin,
        salesInfoY,
        { align: 'right' }
      );
    }
    if (receiptData?.receipt_date) {
      const dateStr = new Date(receiptData.receipt_date).toLocaleDateString(
        'en-US',
        { dateStyle: 'medium' }
      ); // Shorter date format
      doc.text(`Date: ${dateStr}`, pageWidth - margin, salesInfoY + 12, {
        align: 'right',
      });
    }

    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 12;

    // Sales Rep
    doc.setFont(mainFont, 'bold');
    doc.text('Sales Rep: ', margin, yPosition);
    doc.setFont(mainFont, 'normal');
    if (receiptData?.sales_rep)
      doc.text(String(receiptData.sales_rep), margin + 60, yPosition);
    yPosition += 8;
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 15;

    // Table
    const headers = [
      'Service Date',
      'Product/Service',
      'Description',
      'Qty',
      'Rate',
      'Amount',
    ];
    const columnWidths = [70, 90, 160, 30, 50, 50];
    let xPosition = margin;

    doc.setFontSize(mainFontSize); // Smaller table font
    doc.setFont(mainFont, 'bold');
    xPosition = margin;
    headers.forEach((header, i) => {
      doc.text(header, xPosition + 5, yPosition);
      xPosition += columnWidths[i];
    });
    yPosition += 12;
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 12;

    // Table Body
    doc.setFont(mainFont, 'normal');
    (receiptData?.sales_items  || []).forEach((item: any) => {
      xPosition = margin + 5;
      doc.text(
        item?.service_date ? String(item.service_date) : '-',
        xPosition,
        yPosition
      );
      xPosition += columnWidths[0];
      const productLines = doc.splitTextToSize(
        item?.product_service ? String(item.product_service) : '',
        columnWidths[1]
      );
      doc.text(productLines, xPosition, yPosition);
      xPosition += columnWidths[1];
      let description = item?.description
        ? String(item.description).substring(0, 100)
        : '';
      if (
        item?.firearm &&
        Array.isArray(item.serial_number) &&
        item.serial_number.length
      ) {
        description += `\nSerial#: ${item.serial_number
          .join(', ')
          .substring(0, 50)}`;
      }
      const descLines = doc.splitTextToSize(description, columnWidths[2]);
      doc.text(descLines, xPosition, yPosition);
      xPosition += columnWidths[2] + 5;
      doc.text(
        item?.qty !== undefined ? String(item.qty) : '-',
        xPosition,
        yPosition,
        { align: 'center' }
      );
      xPosition += columnWidths[3] + 5;
      let rate =
        typeof item?.rate === 'number' ? item.rate : parseFloat(item?.rate);
      doc.text(!isNaN(rate) ? rate.toFixed(2) : '-', xPosition, yPosition, {
        align: 'center',
      });
      xPosition += columnWidths[4];
      let amount =
        typeof item?.amount === 'number'
          ? item.amount
          : parseFloat(item?.amount);
      doc.text(!isNaN(amount) ? amount.toFixed(2) : '-', xPosition, yPosition, {
        align: 'center',
      });
      yPosition += Math.max(12, descLines.length * 12);
      if (yPosition > 500) {
        // Adjusted for A5
        doc.addPage();
        yPosition = 20;
      }
    });

    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 30;

    // Message and Totals
    doc.setFontSize(mainFontSize);
    doc.setFont(mainFont, 'normal');
    if (receiptData?.message_on_receipt) {
      doc.text(
        String(receiptData.message_on_receipt).substring(0, 200),
        margin,
        yPosition
      ); // Limit message length
    }

    const totalsX = pageWidth - margin - 75;
    const totalValuesX = pageWidth - margin - 2;
    const subtotal =
      typeof receiptData?.subtotal === 'number'
        ? receiptData.subtotal
        : parseFloat(receiptData?.subtotal) || 0;
    const salesTaxRate =
      typeof receiptData?.sales_tax_rate === 'number'
        ? receiptData.sales_tax_rate
        : parseFloat(receiptData?.sales_tax_rate) || 0;
    const total =
      typeof receiptData?.total === 'number'
        ? receiptData.total
        : parseFloat(receiptData?.total) || 0;
    const balanceDue =
      typeof receiptData?.balance_due === 'number'
        ? receiptData.balance_due
        : parseFloat(receiptData?.balance_due) || 0;

    const totals = [
      `Subtotal: `,
      `Tax (${(salesTaxRate * 100).toFixed(2)}%): `,
      `Total: `,
      `Balance Due: `,
    ];
    const totalValues = [
      `$${subtotal.toFixed(2)}`,
      `$${(subtotal * salesTaxRate).toFixed(2)}`,
      `$${total.toFixed(2)}`,
      `$${balanceDue.toFixed(2)}`,
    ];
    yPosition -= 15;
    doc.setFontSize(mainFontSize);
    totals.forEach((line, i) => {
      doc.text(line, totalsX, yPosition + i * 12, { align: 'right' });
    });
    totalValues.forEach((line, i) => {
      doc.text(line, totalValuesX, yPosition + i * 12, { align: 'right' });
    });

    return doc;
  }
  savePdfFile(
    doc: jsPDF,
    receipt_data: SalesReceipt,
    company_info: CompanyInfo
  ): void {
    const namePrefix =
      company_info?.company_name?.trim() || company_info?.legal_name?.trim() || 'receipt';
    doc.save(
      `${namePrefix}_receipt_${
        receipt_data?.sales_receipt_number || 'unknown'
      }.pdf`
    );
  }

  emailPdfFile(doc: jsPDF): string {
    // Returns the PDF as a base64 string for email attachment
    return doc.output('datauristring');
  }
}
